import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { trackEvent } from '../services/analytics';
import SubscriptionGate from '../components/SubscriptionGate';

function Stars({ rating, size = 14 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1,2,3,4,5].map(i => <span key={i} style={{ fontSize: size, color: i <= Math.round(rating) ? '#f59e0b' : '#ddd' }}>&#9733;</span>)}
    </span>
  );
}

function formatDate(d) { if (!d) return ''; return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }

function RatingBar({ label, rating, total, color, isYou }) {
  const pct = rating ? (rating / 5) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderLeft: isYou ? '3px solid var(--green)' : 'none', paddingLeft: isYou ? 12 : 0 }}>
      <div style={{ width: 120, flexShrink: 0 }}>
        <div style={{ fontWeight: isYou ? 700 : 600, fontSize: 13, color: 'var(--text)' }}>{label} {isYou && <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 600 }}>(You)</span>}</div>
        <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{total} reviews</div>
      </div>
      <div style={{ flex: 1, height: 8, background: '#f1efeb', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.6s ease' }} /></div>
      <div style={{ width: 50, textAlign: 'right' }}><span style={{ fontWeight: 800, fontSize: 16, color }}>{rating ? rating.toFixed(1) : '-'}</span></div>
    </div>
  );
}

const SECTION_ICONS = { 'rating comparison': '📊', 'rating': '📊', 'strengths': '💪', 'strength': '💪', 'weaknesses': '⚠️', 'weakness': '⚠️', 'opportunities': '🎯', 'opportunity': '🎯', 'threats': '🔍', 'threat': '🔍' };
function getSectionIcon(title) { const l = title.toLowerCase(); for (const [k, icon] of Object.entries(SECTION_ICONS)) { if (l.includes(k)) return icon; } return '📋'; }
function getSectionColor(title) { const l = title.toLowerCase(); if (l.includes('strength')) return '#16a34a'; if (l.includes('weakness')) return '#dc2626'; if (l.includes('opportunit')) return '#2563eb'; if (l.includes('threat')) return '#d97706'; return '#6b7280'; }
function renderInlineMarkdown(text) { return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : p); }

function AnalysisRenderer({ text }) {
  const lines = text.split('\n');
  const sections = [];
  let currentSection = null;
  let bodyLines = [];
  const flushSection = () => { if (currentSection) sections.push({ title: currentSection, body: bodyLines.join('\n').trim() }); else if (bodyLines.length > 0) { const intro = bodyLines.join('\n').trim(); if (intro) sections.push({ title: null, body: intro }); } bodyLines = []; };
  for (const line of lines) {
    const hm = line.match(/^#{1,3}\s+(.+)/);
    const bh = !hm && line.match(/^(?:\d+\.\s*)?\*\*([^*]+)\*\*\s*$/);
    if (hm) { flushSection(); currentSection = hm[1].replace(/\*\*/g, '').trim(); }
    else if (bh) { flushSection(); currentSection = bh[1].trim(); }
    else bodyLines.push(line);
  }
  flushSection();
  const filtered = sections.filter(s => { if (!s.title) return s.body.length > 0; return !s.title.toLowerCase().startsWith('competitive analysis'); });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {filtered.map((section, i) => {
        if (!section.title) return <div key={i} style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text)', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>{section.body.split('\n').map((l, j) => <p key={j} style={{ margin: '0 0 6px' }}>{renderInlineMarkdown(l)}</p>)}</div>;
        const color = getSectionColor(section.title);
        const icon = getSectionIcon(section.title);
        const bodyParagraphs = section.body.split('\n').filter(l => l.trim());
        return (
          <div key={i} style={{ padding: '14px 16px', marginBottom: 8, borderRadius: 10, background: `${color}08`, borderLeft: `3px solid ${color}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><span style={{ fontSize: 16 }}>{icon}</span><span style={{ fontSize: 14, fontWeight: 700, color }}>{section.title}</span></div>
            {bodyParagraphs.map((line, j) => { const t = line.trim(); if (t.startsWith('- ')) return <div key={j} style={{ display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.6, color: 'var(--text)', padding: '2px 0 2px 24px' }}><span style={{ color, flexShrink: 0 }}>-</span><span>{renderInlineMarkdown(t.slice(2))}</span></div>; return <p key={j} style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '0 0 4px', paddingLeft: 24 }}>{renderInlineMarkdown(t)}</p>; })}
          </div>
        );
      })}
    </div>
  );
}

function CompetitorCard({ comp, onSync, onRemove, isHospitality, isSubscribed }) {
  const [syncing, setSyncing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const handleSync = async () => { setSyncing(true); try { await onSync(comp.id); } finally { setSyncing(false); } };
  const handleRemove = async () => { if (!window.confirm(`Remove ${comp.name}?`)) return; setRemoving(true); try { await onRemove(comp.id); } finally { setRemoving(false); } };

  return (
    <div className="card" style={{ padding: '20px 24px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 14 }}>{isHospitality ? '🏠' : '🔍'}</span>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{comp.name}</h3>
            {comp.current_rating && <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: comp.current_rating >= 4.5 ? 'rgba(22,163,74,0.1)' : comp.current_rating >= 4 ? 'rgba(245,158,11,0.1)' : 'rgba(220,38,38,0.1)', color: comp.current_rating >= 4.5 ? '#16a34a' : comp.current_rating >= 4 ? '#d97706' : '#dc2626' }}>{comp.current_rating.toFixed(1)} &#9733;</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{comp.total_reviews} reviews{comp.last_synced_at && ` - Last synced ${formatDate(comp.last_synced_at)}`}</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <SubscriptionGate isSubscribed={isSubscribed} feature="Syncing competitor data">
            <button className="btn btn-ghost btn-sm" onClick={handleSync} disabled={syncing} style={{ fontSize: 11 }}>{syncing ? 'Syncing...' : 'Sync'}</button>
          </SubscriptionGate>
          <button className="btn btn-ghost btn-sm" onClick={handleRemove} disabled={removing} style={{ fontSize: 11, color: '#dc2626' }}>Remove</button>
        </div>
      </div>
      {comp.reviews && comp.reviews.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => setShowReviews(!showReviews)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--green)', fontWeight: 600, padding: 0 }}>{showReviews ? 'Hide' : 'Show'} recent reviews ({comp.reviews.length})</button>
          {showReviews && <div style={{ marginTop: 10 }}>{comp.reviews.map((r, i) => <div key={i} style={{ padding: '10px 12px', marginBottom: 6, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)' }}><div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}><span style={{ fontWeight: 600, fontSize: 12 }}>{r.reviewer_name || 'Anonymous'}</span><Stars rating={r.rating} size={11} />{r.review_date && <span style={{ fontSize: 10, color: 'var(--text-light)', marginLeft: 'auto' }}>{r.review_date}</span>}</div>{r.review_text && <p style={{ fontSize: 12, color: 'var(--text-light)', margin: 0, lineHeight: 1.5 }}>"{r.review_text}"</p>}</div>)}</div>}
        </div>
      )}
    </div>
  );
}

function AddCompetitorForm({ onAdd, isHospitality, isSubscribed }) {
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [validationError, setValidationError] = useState('');

  const validateUrl = (input) => {
    if (!input.trim()) return '';
    if (isHospitality) { if (input.includes('airbnb.co.uk/rooms/') || input.includes('airbnb.com/rooms/')) return ''; return 'Please paste an Airbnb listing URL (e.g. airbnb.co.uk/rooms/12345)'; }
    else { if (input.includes('google.com/maps') || input.includes('maps.google') || input.includes('g.page') || input.includes('goo.gl/maps')) return ''; return 'Please paste a Google Maps URL'; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateUrl(url);
    if (error) { setValidationError(error); return; }
    setAdding(true); setValidationError('');
    try { await onAdd(url.trim()); setUrl(''); } finally { setAdding(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
      <div style={{ flex: 1 }}>
        <label className="form-label">Add Competitor</label>
        <input className="form-input" type="text" value={url} onChange={e => { setUrl(e.target.value); setValidationError(''); }} placeholder={isHospitality ? 'Paste an Airbnb listing URL (e.g. airbnb.co.uk/rooms/12345)' : 'Paste a Google Maps URL...'} disabled={adding} style={validationError ? { borderColor: '#ef4444' } : {}} />
        {validationError && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>{validationError}</span>}
      </div>
      <SubscriptionGate isSubscribed={isSubscribed} feature="Adding competitors">
        <button className="btn btn-primary" type="submit" disabled={adding || !url.trim()} style={{ height: 42, whiteSpace: 'nowrap' }}>{adding ? 'Adding...' : '+ Add'}</button>
      </SubscriptionGate>
    </form>
  );
}

export default function CompetitorsPage() {
  const [competitors, setCompetitors] = useState([]);
  const [business, setBusiness] = useState(null);
  const [user, setUser] = useState(null);
  const [yourRating, setYourRating] = useState(null);
  const [yourCount, setYourCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const showToast = (message, isError = false) => { setToast({ message, isError }); setTimeout(() => setToast(null), 4000); };

  const fetchData = useCallback(async () => {
    try {
      const [comps, businesses, me] = await Promise.all([api.get('/competitors'), api.getBusinesses(), api.getMe()]);
      setCompetitors(comps);
      if (businesses.length > 0) setBusiness(businesses[0]);
      setUser(me);
      try { const biz = businesses.length > 0 ? businesses[0] : null; if (biz && biz.google_rating) { setYourRating(biz.google_rating); setYourCount(biz.google_total_reviews || 0); } else { const reviews = await api.getReviews(); if (reviews && reviews.length > 0) { const total = reviews.reduce((sum, r) => sum + (r.rating || 0), 0); setYourRating(parseFloat((total / reviews.length).toFixed(2))); setYourCount(reviews.length); } } } catch {}
      try { const insightData = await api.get('/competitors/insights'); setInsights(insightData.insights || []); } catch { setInsights([]); }
    } catch (err) { showToast('Failed to load data', true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isHospitality = business?.business_category === 'hospitality';
  const isSubscribed = ['active', 'trialing'].includes(user?.subscription_status);

  const handleAdd = async (url) => { try { let result; if (isHospitality) { result = await api.post('/competitors/airbnb', { airbnb_url: url }); trackEvent('competitor_added', { source: 'airbnb' }); showToast(`Added ${result.name} (${result.reviews_fetched} reviews fetched)`); } else { result = await api.post('/competitors', { google_maps_url: url }); trackEvent('competitor_added', { source: 'google' }); showToast(result.message); } await fetchData(); } catch (err) { showToast(err.message || 'Failed to add competitor', true); } };
  const handleSync = async (id) => { try { let result; if (isHospitality) { result = await api.post(`/competitors/airbnb/sync/${id}`, {}); showToast(`Synced ${result.name} (${result.reviews_fetched} reviews)`); } else { result = await api.post(`/competitors/${id}/sync`, {}); showToast(result.message); } await fetchData(); } catch (err) { showToast('Sync failed: ' + err.message, true); } };
  const handleSyncAll = async () => { setSyncingAll(true); try { if (isHospitality) { let synced = 0; for (const comp of competitors) { try { await api.post(`/competitors/airbnb/sync/${comp.id}`, {}); synced++; } catch {} } showToast(`Synced ${synced} competitors`); await fetchData(); } else { const result = await api.post('/competitors/sync-all', {}); showToast(`Synced ${result.synced} competitors`); await fetchData(); } } catch (err) { showToast('Sync failed: ' + err.message, true); } finally { setSyncingAll(false); } };
  const handleRemove = async (id) => { try { await api.request(`/competitors/${id}`, { method: 'DELETE' }); setCompetitors(prev => prev.filter(c => c.id !== id)); showToast('Competitor removed'); } catch (err) { showToast('Remove failed: ' + err.message, true); } };
  const handleAnalyse = async () => { setAnalysing(true); setAnalysis(null); try { const result = await api.get('/competitors/analysis'); setAnalysis(result); } catch (err) { showToast(err.message || 'Analysis failed', true); } finally { setAnalysing(false); } };
  const handleRefreshInsights = async () => {
    setInsightsLoading(true);
    try {
      await api.post('/competitors/insights/refresh', {});
      const data = await api.get('/competitors/insights');
      setInsights(data.insights || []);
      showToast('Insights refreshed');
    } catch (err) {
      const msg = err?.response?.status === 429
        ? 'Rate limit reached — try again in a few minutes'
        : err.message || 'Refresh failed';
      showToast(msg, true);
    } finally {
      setInsightsLoading(false);
    }
  };
  const handleDismissInsight = async (id) => { try { await api.post(`/competitors/insights/${id}/dismiss`, {}); setInsights(prev => prev.filter(i => i.id !== id)); showToast('Insight dismissed'); } catch { showToast('Failed to dismiss', true); } };

  const COLORS = ['#16a34a', '#2563eb', '#9333ea', '#d97706', '#dc2626'];
  if (loading) return <div className="loading">Loading competitors...</div>;
  const activeInsights = insights.filter(i => !i.dismissed);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Competitors</h1>
          <p className="page-subtitle">{isHospitality ? 'Benchmark your property against nearby Airbnb listings' : 'Benchmark your reviews against local competitors'}</p>
        </div>
        {competitors.length > 0 && (
          <div style={{ display: 'flex', gap: 10 }}>
            <SubscriptionGate isSubscribed={isSubscribed} feature="Syncing competitors">
              <button className="btn btn-ghost" onClick={handleSyncAll} disabled={syncingAll}>{syncingAll ? 'Syncing...' : 'Sync All'}</button>
            </SubscriptionGate>
            <SubscriptionGate isSubscribed={isSubscribed} feature="Running AI analysis">
              <button className="btn btn-primary" onClick={handleAnalyse} disabled={analysing}>{analysing ? 'Analysing...' : 'AI Analysis'}</button>
            </SubscriptionGate>
          </div>
        )}
      </div>

      {competitors.length < 5 && (
        <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <AddCompetitorForm onAdd={handleAdd} isHospitality={isHospitality} isSubscribed={isSubscribed} />
          <p style={{ fontSize: 11, color: 'var(--text-light)', margin: '8px 0 0' }}>{isHospitality ? 'Find a competing Airbnb listing, copy the URL, and paste it above. Up to 5 competitors.' : 'Find a competitor on Google Maps, copy the URL, and paste it above. Up to 5 competitors.'}</p>
        </div>
      )}

      {competitors.length > 0 && (
        <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, margin: '0 0 12px', color: 'var(--text-light)', letterSpacing: 0.5, textTransform: 'uppercase' }}>Rating Comparison</h3>
          <RatingBar label={business?.name || 'Your Property'} rating={yourRating} total={yourCount} color="#16a34a" isYou />
          {competitors.map((comp, i) => <RatingBar key={comp.id} label={comp.name} rating={comp.current_rating} total={comp.total_reviews} color={COLORS[(i + 1) % COLORS.length]} />)}
        </div>
      )}

      {competitors.length > 0 && (
        <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>💡</span>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{isHospitality ? 'Competitor Insights' : 'Competitor Weaknesses'}</h3>
              {activeInsights.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', background: 'rgba(22,163,74,0.08)', padding: '2px 8px', borderRadius: 10 }}>{activeInsights.length}</span>}
            </div>
            <SubscriptionGate isSubscribed={isSubscribed} feature="Refreshing competitor insights">
              <button className="btn btn-ghost btn-sm" onClick={handleRefreshInsights} disabled={insightsLoading} style={{ fontSize: 11 }}>{insightsLoading ? 'Analysing...' : 'Refresh Insights'}</button>
            </SubscriptionGate>
          </div>
          {activeInsights.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {activeInsights.map((insight) => {
                const dotColor = insight.mention_count >= 4 ? '#dc2626' : insight.mention_count >= 2 ? '#f59e0b' : '#6b7280';
                return (
                  <div key={insight.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, textTransform: 'capitalize' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                        {insight.theme}
                        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-light)' }}>{insight.mention_count} mention{insight.mention_count !== 1 ? 's' : ''}</span>
                      </div>
                      <p style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.5, margin: '4px 0' }}>{insight.opportunity_text}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-light)', margin: '4px 0 0' }}>{insight.evidence}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-light)', margin: '2px 0 0', fontStyle: 'italic' }}>Affected: {insight.competitor_names}</p>
                    </div>
                    <SubscriptionGate isSubscribed={isSubscribed} feature="Dismissing insights">
                      <button onClick={() => handleDismissInsight(insight.id)} title="Dismiss" style={{ background: 'none', border: 'none', color: 'var(--border)', fontSize: 14, cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}>✕</button>
                    </SubscriptionGate>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0, lineHeight: 1.5 }}>{isHospitality ? 'No insights found yet. Sync competitor listings and click "Refresh Insights".' : 'No weakness patterns found yet. Sync competitor reviews and click "Refresh Insights".'}</p>
          )}
          <p style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 14, marginBottom: 0 }}>These insights are automatically used to enhance your AI review responses.</p>
        </div>
      )}

      {analysing && <div className="card" style={{ padding: '24px', marginBottom: 20, textAlign: 'center' }}><div style={{ fontSize: 14, color: 'var(--text-light)', marginBottom: 8 }}>{isHospitality ? 'Analysing your reviews against competitor listings...' : 'Analysing your reviews against competitors...'}</div><div style={{ fontSize: 12, color: 'var(--text-light)' }}>This may take 10-15 seconds</div></div>}

      {analysis && (
        <div className="card" style={{ padding: '24px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}><h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Competitive Analysis</h3><span style={{ fontSize: 11, color: 'var(--text-light)' }}>{formatDate(analysis.generated_at)}</span></div>
          <AnalysisRenderer text={analysis.analysis} />
          <SubscriptionGate isSubscribed={isSubscribed} feature="Regenerating analysis">
            <button className="btn btn-ghost btn-sm" onClick={handleAnalyse} disabled={analysing} style={{ marginTop: 16, fontSize: 12 }}>Regenerate Analysis</button>
          </SubscriptionGate>
        </div>
      )}

      {competitors.length > 0 && (
        <div>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-light)', letterSpacing: 0.5, textTransform: 'uppercase', margin: '0 0 12px' }}>Your Competitors ({competitors.length}/5)</h3>
          {competitors.map(comp => <CompetitorCard key={comp.id} comp={comp} onSync={handleSync} onRemove={handleRemove} isHospitality={isHospitality} isSubscribed={isSubscribed} />)}
        </div>
      )}

      {competitors.length === 0 && (
        <div className="card" style={{ padding: '32px', textAlign: 'center', marginTop: 8 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>{isHospitality ? '🏠' : '🏆'}</div>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>Know your competition</h3>
          <p style={{ fontSize: 13, color: 'var(--text-light)', maxWidth: 400, margin: '0 auto 16px', lineHeight: 1.6 }}>{isHospitality ? 'Add up to 5 competing Airbnb listings to compare ratings, reviews, and get AI-powered insights.' : 'Add up to 5 local competitors to compare ratings, review volume, and get AI-powered insights.'}</p>
        </div>
      )}

      {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.message}</div>}
    </div>
  );
}
